import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Trash, Plus, Image as ImageIcon } from "@phosphor-icons/react";
import { api, errorMessage } from "@/lib/api";
import { AdminCard, adminBtn, adminBtnGhost, adminInput } from "./AdminLayout";

const F = ({ label, children, hint }) => (
  <label className="block text-sm">
    <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
    {children}
    {hint && <span className="mt-1 block text-[11px] text-muted-foreground">{hint}</span>}
  </label>
);

export default function AdminPoems() {
  const qc = useQueryClient();
  const { data: poems = [], isLoading } = useQuery({ queryKey: ["admin-poems"], queryFn: async () => (await api.get("/admin/poems")).data });
  const [form, setForm] = useState({ title_ta: "", title_en: "", file: null });
  const [busy, setBusy] = useState(false);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["admin-poems"] }); qc.invalidateQueries({ queryKey: ["poems"] }); };

  const save = useMutation({
    mutationFn: ({ poem_id, ...body }) => api.put(`/admin/poems/${poem_id}`, body),
    onSuccess: () => { refresh(); toast.success("Poem updated"); },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const add = async (e) => {
    e.preventDefault();
    if (!form.file) return toast.error("Choose a poem page image");
    const fd = new FormData();
    fd.append("title_ta", form.title_ta);
    fd.append("title_en", form.title_en);
    fd.append("file", form.file);
    setBusy(true);
    try {
      await api.post("/admin/poems", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ title_ta: "", title_en: "", file: null });
      e.target.reset();
      refresh();
      toast.success("Poem page added — copyright line stamped on the image");
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const replaceImage = async (poem_id, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/admin/poems/${poem_id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      refresh();
      toast.success("Page replaced");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const move = async (index, dir) => {
    const next = [...poems];
    const target = index + dir;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    try {
      await api.post("/admin/poems/reorder", { ids: next.map((p) => p.poem_id) });
      refresh();
    } catch (err) { toast.error(errorMessage(err)); }
  };

  const remove = async (poem) => {
    if (!window.confirm(`Remove “${poem.title_ta}” from the gallery?`)) return;
    try {
      await api.delete(`/admin/poems/${poem.poem_id}`);
      refresh();
      toast.success("Poem removed");
    } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="space-y-6" data-testid="admin-poems-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-medium tracking-tight">Poem gallery</h1>
          <p className="mt-1 text-sm text-muted-foreground">Pages shown in “From the pages” on the homepage. Every upload is stamped with © 2026 Shunmugavel · Agamali Kaeleer · ShansoBooks.</p>
        </div>
      </div>

      <AdminCard title="Add a poem page">
        <form onSubmit={add} className="grid items-end gap-3 sm:grid-cols-4" data-testid="poem-add-form">
          <F label="Tamil title"><input required value={form.title_ta} onChange={(e) => setForm((f) => ({ ...f, title_ta: e.target.value }))} className={adminInput} data-testid="poem-title-ta-input" /></F>
          <F label="English gloss"><input value={form.title_en} onChange={(e) => setForm((f) => ({ ...f, title_en: e.target.value }))} className={adminInput} data-testid="poem-title-en-input" /></F>
          <F label="Page image" hint="JPG or PNG · portrait works best">
            <input required type="file" accept="image/*" onChange={(e) => setForm((f) => ({ ...f, file: e.target.files[0] }))} className="block w-full text-xs" data-testid="poem-file-input" />
          </F>
          <button type="submit" disabled={busy} className={adminBtn} data-testid="poem-add-button"><Plus size={15} weight="bold" /> {busy ? "Uploading…" : "Add page"}</button>
        </form>
      </AdminCard>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : poems.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="poems-empty">No poem pages yet.</p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {poems.map((p, i) => (
            <AdminCard key={p.poem_id} className="h-full">
              <div className="flex gap-4">
                <img src={p.image_url} alt={p.title_ta} className="h-36 w-24 shrink-0 rounded border border-border object-cover object-top" data-testid={`poem-thumb-${p.poem_id}`} />
                <div className="min-w-0 flex-1 space-y-2">
                  <input defaultValue={p.title_ta} key={`ta-${p.poem_id}-${p.title_ta}`} onBlur={(e) => e.target.value !== p.title_ta && save.mutate({ poem_id: p.poem_id, title_ta: e.target.value, title_en: p.title_en, active: p.active, sort_order: p.sort_order })}
                    className={adminInput} data-testid={`poem-edit-ta-${p.poem_id}`} />
                  <input defaultValue={p.title_en} key={`en-${p.poem_id}-${p.title_en}`} placeholder="English gloss" onBlur={(e) => e.target.value !== p.title_en && save.mutate({ poem_id: p.poem_id, title_ta: p.title_ta, title_en: e.target.value, active: p.active, sort_order: p.sort_order })}
                    className={adminInput} data-testid={`poem-edit-en-${p.poem_id}`} />
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <label className="flex items-center gap-1.5 text-xs">
                      <input type="checkbox" checked={p.active} onChange={(e) => save.mutate({ poem_id: p.poem_id, title_ta: p.title_ta, title_en: p.title_en, active: e.target.checked, sort_order: p.sort_order })} data-testid={`poem-active-${p.poem_id}`} />
                      Visible
                    </label>
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className={adminBtnGhost} aria-label="Move up" data-testid={`poem-up-${p.poem_id}`}><ArrowUp size={14} weight="bold" /></button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === poems.length - 1} className={adminBtnGhost} aria-label="Move down" data-testid={`poem-down-${p.poem_id}`}><ArrowDown size={14} weight="bold" /></button>
                    <label className={`${adminBtnGhost} cursor-pointer`} data-testid={`poem-replace-label-${p.poem_id}`}>
                      <ImageIcon size={14} weight="bold" /> Replace
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => replaceImage(p.poem_id, e.target.files[0])} data-testid={`poem-replace-${p.poem_id}`} />
                    </label>
                    <button type="button" onClick={() => remove(p)} className={`${adminBtnGhost} text-destructive`} aria-label="Delete" data-testid={`poem-delete-${p.poem_id}`}><Trash size={14} weight="bold" /></button>
                  </div>
                </div>
              </div>
            </AdminCard>
          ))}
        </div>
      )}
    </div>
  );
}
